import { cn } from '@ai-tutor/ui';
import type { BuildPreviewSource } from '@/lib/api-client';

/** At most this many section chips in all, so the card never scrolls. */
const MAX_CHIPS = 9;
/** At most this many sources listed; the rest are counted. */
const MAX_SOURCES = 3;
/** Each source heading and chip arrives this long after the one before (ms).
 *  Slow on purpose: planning takes a minute or two, so the material is
 *  pulled out over roughly the first ten seconds of it, not all at once. */
const POP_STEP = 900;

interface BuildPreviewProps {
  sources: BuildPreviewSource[];
  /** The student's own files vs sources found for a topic: words the title. */
  hasUserSources?: boolean;
  className?: string;
}

/**
 * "Found in your material" (the student's files) or "Found for your topic"
 * (textbook sections and pages found for a topic-only lesson): the sections
 * the sources were read into, shown while the lesson is planned from them.
 * Titles only, as chips, within a fixed budget (`MAX_CHIPS` across
 * `MAX_SOURCES`), with "+N more" - a glance at the raw material, not the plan
 * (the mind map is the plan). Headings and chips pop in one after another, as
 * if being pulled out of the material (no motion under reduced motion).
 */
export function BuildPreview({
  sources,
  hasUserSources = true,
  className,
}: BuildPreviewProps) {
  const merged = mergeByHeading(sources);
  const shown = merged.slice(0, MAX_SOURCES);
  const perSource = Math.max(2, Math.floor(MAX_CHIPS / shown.length));
  const hiddenSources = merged.length - shown.length;
  // Where each source's run of pops starts: its heading, then its chips.
  const starts = shown.reduce<number[]>(
    (acc, s, i) => [
      ...acc,
      i === 0
        ? 0
        : acc[i - 1] +
          1 +
          Math.min(perSource, shown[i - 1].sections?.length ?? 0),
    ],
    []
  );
  return (
    <section
      aria-labelledby='build-preview-title'
      className={cn('flex min-h-0 flex-col text-left', className)}
    >
      <h3
        id='build-preview-title'
        className='font-mono text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground'
      >
        {hasUserSources ? 'Found in your material' : 'Found for your topic'}
      </h3>
      <ul className='mt-3 min-h-0 space-y-4 overflow-hidden'>
        {shown.map((source, i) => (
          <PreviewSource
            key={`${source.name}-${i}`}
            source={source}
            limit={perSource}
            start={starts[i]}
          />
        ))}
      </ul>
      {hiddenSources > 0 && (
        <p className='mt-3 font-mono text-xs tabular-nums text-muted-foreground'>
          +{hiddenSources} more {hiddenSources === 1 ? 'source' : 'sources'}
        </p>
      )}
    </section>
  );
}

/** A textbook source is named "Book · 8.1 …, 8.2 …"; its sections are the
 *  chips beneath, so the heading keeps the book alone. */
function sourceHeading(name: string): string {
  return name.split(' · ')[0] || name;
}

function sameTitle(a: string, b: string): boolean {
  const norm = (t: string) => t.toLowerCase().replace(/\W+/g, ' ').trim();
  return norm(a) === norm(b);
}

/** Two chapters of one book are one source to a reader: one heading, their
 *  sections together, in order. */
function mergeByHeading(sources: BuildPreviewSource[]): BuildPreviewSource[] {
  const byHeading = new Map<string, BuildPreviewSource>();
  for (const source of sources) {
    const heading = sourceHeading(source.name);
    const seen = byHeading.get(heading);
    if (!seen) {
      byHeading.set(heading, { ...source, name: heading });
      continue;
    }
    byHeading.set(heading, {
      ...seen,
      sections: [...(seen.sections ?? []), ...(source.sections ?? [])],
      section_count: seen.section_count + source.section_count,
    });
  }
  return [...byHeading.values()];
}

const pop = (step: number) => ({ animationDelay: `${step * POP_STEP}ms` });
const POP = 'animate-pop-in motion-reduce:animate-none';

function PreviewSource({
  source,
  limit,
  start,
}: {
  source: BuildPreviewSource;
  limit: number;
  /** This source's place in the pop sequence. */
  start: number;
}) {
  const heading = sourceHeading(source.name);
  // A written document's one section is often titled like the document
  // itself: shown, the same line appeared twice ("Benzene: ..." over
  // "Benzene: ...").
  const all = source.sections ?? [];
  const own = all.filter(s => !sameTitle(s.title, heading));
  const sections = own.slice(0, limit);
  const more =
    source.section_count - (all.length - own.length) - sections.length;
  return (
    <li className='min-w-0'>
      <p
        title={source.name}
        style={pop(start)}
        className={cn('truncate text-sm font-medium text-foreground', POP)}
      >
        {heading}
      </p>
      {sections.length > 0 && (
        <ul className='mt-2 flex flex-wrap gap-1.5'>
          {sections.map((section, i) => (
            <li
              key={`${section.title}-${i}`}
              title={section.title}
              style={pop(start + 1 + i)}
              className={cn(
                'max-w-full truncate rounded-md border border-border bg-muted/60 px-2 py-1 text-xs text-foreground',
                POP
              )}
            >
              {section.title}
            </li>
          ))}
          {more > 0 && (
            <li
              style={pop(start + 1 + sections.length)}
              className={cn(
                'px-1 py-1 font-mono text-xs tabular-nums text-muted-foreground',
                POP
              )}
            >
              +{more} more
            </li>
          )}
        </ul>
      )}
    </li>
  );
}
